import type { ResearchEffectKey, ResearchNodeDef, ResearchTree } from "../schema/research.js";
import { hqTimeSec } from "./curves.js";

type Row = [key: string, de: string, en: string, effect: ResearchEffectKey, perLevel: number, maxLevel: number, tier: number, requires: string[]];

/** 25 nodes per tree (20-base.md R14). `requires` names keys inside the same tree. */
const TREES: Record<ResearchTree, Row[]> = {
  economy: [
    ["rations_1", "Hydroponik I", "Hydroponics I", "production.RAT", 4, 5, 1, []],
    ["polymer_1", "Polymersynthese I", "Polymer Synthesis I", "production.POL", 4, 5, 1, []],
    ["storage_1", "Lagerlogistik I", "Storage Logistics I", "storage.capacity", 5, 5, 1, []],
    ["gather_1", "Sammeltrupps I", "Gathering Teams I", "gather.speed", 5, 5, 1, []],
    ["alloy_1", "Schmelzöfen I", "Furnaces I", "production.ALY", 4, 5, 2, ["rations_1"]],
    ["scrip_1", "Schwarzmarktkontakte", "Black Market Contacts", "production.SCR", 5, 5, 2, ["polymer_1"]],
    ["protected_1", "Tresorpanzerung I", "Vault Plating I", "storage.protected", 3, 5, 2, ["storage_1"]],
    ["cargo_1", "Frachtmodule I", "Cargo Modules I", "march.cargo", 5, 5, 2, ["gather_1"]],
    ["rations_2", "Hydroponik II", "Hydroponics II", "production.RAT", 5, 5, 3, ["alloy_1"]],
    ["polymer_2", "Polymersynthese II", "Polymer Synthesis II", "production.POL", 5, 5, 3, ["scrip_1"]],
    ["trade_1", "Handelsrouten", "Trade Routes", "trade.discount", 2, 5, 3, ["scrip_1"]],
    ["gather_2", "Sammeltrupps II", "Gathering Teams II", "gather.speed", 6, 5, 3, ["cargo_1"]],
    ["energy_1", "Fusionskalibrierung I", "Fusion Calibration I", "production.ENC", 4, 5, 4, ["rations_2"]],
    ["storage_2", "Lagerlogistik II", "Storage Logistics II", "storage.capacity", 6, 5, 4, ["protected_1"]],
    ["salvage_1", "Bergungsprotokolle", "Salvage Protocols", "salvage.yield", 5, 5, 4, ["trade_1"]],
    ["alloy_2", "Schmelzöfen II", "Furnaces II", "production.ALY", 5, 5, 4, ["polymer_2"]],
    ["all_1", "Effizienzdoktrin I", "Efficiency Doctrine I", "production.all", 2, 5, 5, ["energy_1", "alloy_2"]],
    ["cargo_2", "Frachtmodule II", "Cargo Modules II", "march.cargo", 6, 5, 5, ["gather_2"]],
    ["protected_2", "Tresorpanzerung II", "Vault Plating II", "storage.protected", 4, 5, 5, ["storage_2"]],
    ["energy_2", "Fusionskalibrierung II", "Fusion Calibration II", "production.ENC", 5, 5, 6, ["all_1"]],
    ["scrip_2", "Kartellverträge", "Cartel Contracts", "production.SCR", 6, 5, 6, ["salvage_1"]],
    ["gather_3", "Sammeltrupps III", "Gathering Teams III", "gather.speed", 8, 5, 6, ["cargo_2"]],
    ["storage_3", "Lagerlogistik III", "Storage Logistics III", "storage.capacity", 8, 5, 7, ["protected_2"]],
    ["all_2", "Effizienzdoktrin II", "Efficiency Doctrine II", "production.all", 3, 5, 7, ["energy_2", "scrip_2"]],
    ["trade_2", "Kartellrabatte", "Cartel Discounts", "trade.discount", 3, 5, 7, ["scrip_2"]],
  ],
  military: [
    ["def_hp_1", "Verbundpanzerung I", "Composite Armor I", "unit.defender.hp", 3, 5, 1, []],
    ["atk_1", "Zielsysteme I", "Targeting Systems I", "unit.attacker.atk", 3, 5, 1, []],
    ["sab_1", "Nadelprotokoll I", "Needle Protocol I", "unit.saboteur.atk", 3, 5, 1, []],
    ["cmd_1", "Kommandolink I", "Command Link I", "unit.commander.hp", 3, 5, 1, []],
    ["def_def_1", "Reaktivplatten I", "Reactive Plates I", "unit.defender.def", 3, 5, 2, ["def_hp_1"]],
    ["crit_1", "Schwachstellenanalyse I", "Weak Point Analysis I", "unit.crit", 1, 5, 2, ["atk_1"]],
    ["sab_crit_1", "Präzisionsklingen", "Precision Blades", "unit.saboteur.crit", 2, 5, 2, ["sab_1"]],
    ["pulse_1", "Impulsverstärker I", "Pulse Amplifier I", "commander.pulse", 4, 5, 2, ["cmd_1"]],
    ["tank_1", "Kettenfahrwerke", "Tracked Drives", "unit.chassis.tank", 3, 5, 3, ["def_def_1"]],
    ["mecha_1", "Servomotoren", "Servo Motors", "unit.chassis.mecha", 3, 5, 3, ["atk_1"]],
    ["tripod_1", "Gyrostabilisatoren", "Gyro Stabilizers", "unit.chassis.tripod", 3, 5, 3, ["sab_crit_1"]],
    ["energy_1", "Energiezellen I", "Energy Cells I", "unit.energy", 3, 5, 3, ["pulse_1"]],
    ["all_atk_1", "Waffendoktrin I", "Weapons Doctrine I", "unit.all.atk", 2, 5, 4, ["mecha_1"]],
    ["all_def_1", "Panzerdoktrin I", "Armor Doctrine I", "unit.all.def", 2, 5, 4, ["tank_1"]],
    ["support_1", "Auraverstärker I", "Aura Amplifier I", "unit.support.aura", 5, 5, 4, ["energy_1"]],
    ["sab_2", "Nadelprotokoll II", "Needle Protocol II", "unit.saboteur.atk", 4, 5, 4, ["tripod_1"]],
    ["all_hp_1", "Strukturdoktrin I", "Structure Doctrine I", "unit.all.hp", 2, 5, 5, ["all_def_1"]],
    ["crit_2", "Schwachstellenanalyse II", "Weak Point Analysis II", "unit.crit", 1.5, 5, 5, ["all_atk_1"]],
    ["pulse_2", "Impulsverstärker II", "Pulse Amplifier II", "commander.pulse", 5, 5, 5, ["support_1"]],
    ["def_hp_2", "Verbundpanzerung II", "Composite Armor II", "unit.defender.hp", 4, 5, 6, ["all_hp_1"]],
    ["atk_2", "Zielsysteme II", "Targeting Systems II", "unit.attacker.atk", 4, 5, 6, ["crit_2"]],
    ["energy_2", "Energiezellen II", "Energy Cells II", "unit.energy", 4, 5, 6, ["pulse_2"]],
    ["all_atk_2", "Waffendoktrin II", "Weapons Doctrine II", "unit.all.atk", 3, 5, 7, ["atk_2"]],
    ["all_def_2", "Panzerdoktrin II", "Armor Doctrine II", "unit.all.def", 3, 5, 7, ["def_hp_2"]],
    ["all_hp_2", "Strukturdoktrin II", "Structure Doctrine II", "unit.all.hp", 3, 5, 7, ["energy_2"]],
    // D136: the 26th military node — each level lets rank S modules go five steps past +15 (up to +40).
    ["overforge", "Überschmiede", "Overforge", "module.overforge", 5, 5, 8, ["all_atk_2"]],
  ],
  development: [
    ["build_1", "Baurobotik I", "Construction Robotics I", "build.speed", 2, 5, 1, []],
    ["research_1", "Datenkompression I", "Data Compression I", "research.speed", 2, 5, 1, []],
    ["xp_1", "Simulationsdaten I", "Simulation Data I", "xp.gain", 4, 5, 1, []],
    ["repair_1", "Feldreparatur I", "Field Repair I", "repair.speed", 4, 5, 1, []],
    ["build_2", "Baurobotik II", "Construction Robotics II", "build.speed", 3, 5, 2, ["build_1"]],
    ["stamina_1", "Ausdauerprotokoll I", "Stamina Protocol I", "stamina.cap", 5, 4, 2, ["xp_1"]],
    ["fab_1", "Fabrikationsrabatt I", "Fabrication Discount I", "fabrication.discount", 2, 5, 2, ["research_1"]],
    ["march_speed_1", "Marschlogistik I", "March Logistics I", "march.speed", 4, 5, 2, ["repair_1"]],
    ["research_2", "Datenkompression II", "Data Compression II", "research.speed", 3, 5, 3, ["fab_1"]],
    ["slots_1", "Zweiter Marschbefehl", "Second March Order", "march.slots", 1, 1, 3, ["march_speed_1"]],
    ["xp_2", "Simulationsdaten II", "Simulation Data II", "xp.gain", 5, 5, 3, ["stamina_1"]],
    ["cdc_1", "Kernextraktion I", "Core Extraction I", "cdc.drop", 5, 5, 3, ["research_2"]],
    ["build_3", "Baurobotik III", "Construction Robotics III", "build.speed", 3, 5, 4, ["build_2"]],
    ["repair_2", "Feldreparatur II", "Field Repair II", "repair.speed", 5, 5, 4, ["slots_1"]],
    ["fab_2", "Fabrikationsrabatt II", "Fabrication Discount II", "fabrication.discount", 2, 5, 4, ["cdc_1"]],
    ["stamina_2", "Ausdauerprotokoll II", "Stamina Protocol II", "stamina.cap", 5, 4, 4, ["xp_2"]],
    ["research_3", "Datenkompression III", "Data Compression III", "research.speed", 4, 5, 5, ["fab_2"]],
    ["march_speed_2", "Marschlogistik II", "March Logistics II", "march.speed", 5, 5, 5, ["repair_2"]],
    ["xp_3", "Simulationsdaten III", "Simulation Data III", "xp.gain", 6, 5, 5, ["stamina_2"]],
    ["build_4", "Baurobotik IV", "Construction Robotics IV", "build.speed", 4, 5, 6, ["build_3"]],
    ["slots_2", "Dritter Marschbefehl", "Third March Order", "march.slots", 1, 1, 6, ["march_speed_2"]],
    ["cdc_2", "Kernextraktion II", "Core Extraction II", "cdc.drop", 6, 5, 6, ["research_3"]],
    ["repair_3", "Feldreparatur III", "Field Repair III", "repair.speed", 6, 5, 7, ["slots_2"]],
    ["research_4", "Datenkompression IV", "Data Compression IV", "research.speed", 4, 5, 7, ["cdc_2"]],
    ["build_5", "Baurobotik V", "Construction Robotics V", "build.speed", 4, 5, 7, ["build_4"]],
  ],
  defense: [
    ["wall_1", "Wallverstärkung I", "Wall Reinforcement I", "wall.hp", 6, 5, 1, []],
    ["turret_1", "Geschützkalibrierung I", "Turret Calibration I", "turret.power", 5, 5, 1, []],
    ["trap_1", "Fallenlager I", "Trap Storage I", "trap.capacity", 8, 5, 1, []],
    ["warning_1", "Frühwarnung I", "Early Warning I", "warning.time", 10, 5, 1, []],
    ["garrison_def_1", "Garnisonsdoktrin I", "Garrison Doctrine I", "garrison.def", 3, 5, 2, ["wall_1"]],
    ["trap_dmg_1", "Sprengfallen I", "Blast Traps I", "trap.damage", 6, 5, 2, ["trap_1"]],
    ["scout_1", "Störsender I", "Jammers I", "scout.resist", 8, 5, 2, ["warning_1"]],
    ["shield_1", "Schildeffizienz I", "Shield Efficiency I", "shield.discount", 3, 5, 2, ["turret_1"]],
    ["wall_2", "Wallverstärkung II", "Wall Reinforcement II", "wall.hp", 7, 5, 3, ["garrison_def_1"]],
    ["turret_2", "Geschützkalibrierung II", "Turret Calibration II", "turret.power", 6, 5, 3, ["shield_1"]],
    ["burn_1", "Löschsysteme I", "Fire Suppression I", "burn.duration", 8, 5, 3, ["scout_1"]],
    ["garrison_atk_1", "Konterdoktrin I", "Counter Doctrine I", "garrison.atk", 3, 5, 3, ["trap_dmg_1"]],
    ["trap_2", "Fallenlager II", "Trap Storage II", "trap.capacity", 10, 5, 4, ["garrison_atk_1"]],
    ["warning_2", "Frühwarnung II", "Early Warning II", "warning.time", 12, 5, 4, ["burn_1"]],
    ["garrison_def_2", "Garnisonsdoktrin II", "Garrison Doctrine II", "garrison.def", 4, 5, 4, ["wall_2"]],
    ["shield_2", "Schildeffizienz II", "Shield Efficiency II", "shield.discount", 4, 5, 4, ["turret_2"]],
    ["wall_3", "Wallverstärkung III", "Wall Reinforcement III", "wall.hp", 8, 5, 5, ["garrison_def_2"]],
    ["turret_3", "Geschützkalibrierung III", "Turret Calibration III", "turret.power", 7, 5, 5, ["shield_2"]],
    ["scout_2", "Störsender II", "Jammers II", "scout.resist", 10, 5, 5, ["warning_2"]],
    ["trap_dmg_2", "Sprengfallen II", "Blast Traps II", "trap.damage", 8, 5, 5, ["trap_2"]],
    ["garrison_atk_2", "Konterdoktrin II", "Counter Doctrine II", "garrison.atk", 4, 5, 6, ["trap_dmg_2"]],
    ["burn_2", "Löschsysteme II", "Fire Suppression II", "burn.duration", 10, 5, 6, ["scout_2"]],
    ["garrison_def_3", "Garnisonsdoktrin III", "Garrison Doctrine III", "garrison.def", 5, 5, 6, ["wall_3"]],
    ["wall_4", "Wallverstärkung IV", "Wall Reinforcement IV", "wall.hp", 10, 5, 7, ["garrison_def_3"]],
    ["turret_4", "Geschützkalibrierung IV", "Turret Calibration IV", "turret.power", 8, 5, 7, ["turret_3"]],
  ],
};

const TREE_NAMES: Record<ResearchTree, [string, string]> = {
  economy: ["Wirtschaft", "Economy"],
  military: ["Militär", "Military"],
  development: ["Entwicklung", "Development"],
  defense: ["Verteidigung", "Defense"],
};

function describe(effect: ResearchEffectKey, perLevel: number): { de: string; en: string } {
  const v = perLevel;
  const map: Partial<Record<ResearchEffectKey, [string, string]>> = {
    "production.RAT": [`+${v}% Rationenproduktion pro Stufe`, `+${v}% Rations production per level`],
    "production.POL": [`+${v}% Polymerproduktion pro Stufe`, `+${v}% Polymer production per level`],
    "production.ALY": [`+${v}% Legierungsproduktion pro Stufe`, `+${v}% Alloy production per level`],
    "production.ENC": [`+${v}% Energiezellenproduktion pro Stufe`, `+${v}% Energy Cell production per level`],
    "production.SCR": [`+${v}% Scrip-Einnahmen pro Stufe`, `+${v}% Scrip income per level`],
    "production.all": [`+${v}% gesamte Produktion pro Stufe`, `+${v}% all production per level`],
    "storage.capacity": [`+${v}% Lagerkapazität pro Stufe`, `+${v}% storage capacity per level`],
    "module.overforge": [`+${v} Aufwertungsstufen für Rang-S-Module (über +15) pro Stufe`, `+${v} enhancement steps for rank S modules (past +15) per level`],
    "storage.protected": [`+${v}% geschützte Ressourcen pro Stufe`, `+${v}% protected resources per level`],
    "build.speed": [`+${v}% Baugeschwindigkeit pro Stufe`, `+${v}% build speed per level`],
    "research.speed": [`+${v}% Forschungsgeschwindigkeit pro Stufe`, `+${v}% research speed per level`],
    "march.slots": ["+1 gleichzeitiger Marsch", "+1 simultaneous march"],
  };
  const found = map[effect];
  if (found) return { de: found[0], en: found[1] };
  return { de: `+${v}% ${effect} pro Stufe`, en: `+${v}% ${effect} per level` };
}

export const RESEARCH_NODES: readonly ResearchNodeDef[] = (Object.keys(TREES) as ResearchTree[]).flatMap((tree) =>
  TREES[tree].map(([key, de, en, effect, perLevel, maxLevel, tier, requires]) => ({
    id: `rsh.${tree}.${key}`,
    tree,
    name: { de, en },
    description: describe(effect, perLevel),
    tier,
    maxLevel,
    requires: requires.map((r) => `rsh.${tree}.${r}`),
    effect: { key: effect, perLevel },
    costWeight: 0.35 + tier * 0.12,
    timeWeight: 0.3 + tier * 0.1,
  })),
);

const nodeMap = new Map(RESEARCH_NODES.map((n) => [n.id, n]));

export function researchNodeById(id: string): ResearchNodeDef {
  const n = nodeMap.get(id);
  if (!n) throw new Error(`unknown research node ${id}`);
  return n;
}

export function researchTreeName(tree: ResearchTree): { de: string; en: string } {
  return { de: TREE_NAMES[tree][0], en: TREE_NAMES[tree][1] };
}

/** Resource cost to research `toLevel` of a node: grows with tier and level. */
/** D118: research badges a level costs — none below tier 4, then tier − 3 per level (tier 7: four). */
export function researchBadges(node: ResearchNodeDef, _toLevel: number): number {
  return node.tier >= 4 ? node.tier - 3 : 0;
}
/** Drone cores that make one research badge at the research lab. */
export const BADGE_FROM_DRONE_CORES = 5;

export function researchCost(node: ResearchNodeDef, toLevel: number): Record<"RAT" | "POL" | "ALY" | "ENC" | "SCR", number> {
  const base = 600 * Math.pow(1.45, node.tier - 1) * Math.pow(1.3, toLevel - 1) * node.costWeight;
  const round = (n: number) => Math.round(n / 10) * 10;
  return {
    RAT: round(base * 1.0),
    POL: round(base * 0.9),
    ALY: node.tier >= 2 ? round(base * 0.45) : 0,
    ENC: node.tier >= 4 ? round(base * 0.15) : 0,
    SCR: round(base * 0.2),
  };
}

/** Research time in seconds: reuses the HQ timer curve at an equivalent level. */
export function researchTimeSec(node: ResearchNodeDef, toLevel: number): number {
  const equivalent = Math.min(30, 2 + node.tier * 3 + toLevel);
  return Math.max(10, Math.round(hqTimeSec(equivalent) * node.timeWeight));
}
